import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import claimEmailTemplateDeleteApi from 'models/core/claimEmailTemplate/delete/Api.ClaimEmailTemplate.Delete';
import ClaimEmailTemplate from 'models/core/claimEmailTemplate/Model.ClaimEmailTemplate';
import claimEmailTemplateSaveApi from 'models/core/claimEmailTemplate/save/Api.ClaimEmailTemplate.Save';
import ClaimStatusCode from 'models/enums/ClaimStatusCode';
import { useMemo } from 'react';
import { claimEmailTemplateIndexRoute } from './ClaimEmailTemplate.Index';

export enum ClaimEmailTriggers {
  ANYSTATUS_SEND_INVOICE = 'ANYSTATUS_Send Invoice',
  OPEN_PENDING_SEND_DOCUMENT = 'OPEN_PENDING_Send Document',
  ANYSTATUS_SEND_EMAIL = 'ANYSTATUS_SEND_EMAIL',
}

export enum ClaimEmailTemplateAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyClaimEmailTemplateAction = Utils.VerifyExtends<
  Module.Claims.ClaimEmailTemplate.Actions,
  Utils.ValueOf<typeof ClaimEmailTemplateAction>
>;
export default function useClaimEmailTemplate() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.ClaimEmailTemplate.Params, Module.Claims.ClaimEmailTemplate.Query>({
    route: claimEmailTemplateIndexRoute,
  });

  const canView =
    permissions.CREATE_EMAILTEMPLATE || permissions.EDIT_EMAILTEMPLATE || permissions.DELETE_EMAILTEMPLATE;
  const templateApi = useApi({ action: ClaimEmailTemplate.list });

  const listApi = useApi({
    action: ClaimEmailTemplate.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => {
      templateApi.execute((b) => b);
      pageRouter.redirect({}, { ...pageRouter.query, list: req });
    },
    wait: !canView,
  });

  const deleteApi = useApi({
    action: claimEmailTemplateDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: claimEmailTemplateSaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const availableStatuses = useMemo(() => {
    const claimStatuses = Object.values({ ...ClaimStatusCode, ...ClaimEmailTriggers })?.map((status) => status);
    const usedStatuses = templateApi?.payload?.data?.map((it) => it.claimStatus) || [];

    return [
      ...claimStatuses.filter((status: any) => !usedStatuses.includes(status)),
      ...[selected?.claimStatus],
    ].filter(Boolean);
  }, [templateApi?.payload?.data, selected]);

  return {
    canView,
    pageRouter,
    listApi,
    saveApi,
    deleteApi,
    selected,
    permissions,
    availableStatuses,
    views: {
      [ClaimEmailTemplateAction.DELETE]: !!(
        pageRouter.params.action === ClaimEmailTemplateAction.DELETE &&
        selected &&
        permissions.DELETE_EMAILTEMPLATE
      ),
      [ClaimEmailTemplateAction.EDIT]: !!(
        pageRouter.params.action === ClaimEmailTemplateAction.EDIT &&
        ((permissions.CREATE_EMAILTEMPLATE && !selected) || (permissions.EDIT_EMAILTEMPLATE && selected))
      ),
    },
  };
}

export function useClaimEmailTemplateProvider() {
  return useDataProvider<ReturnType<typeof useClaimEmailTemplate>>();
}
